import type { EmailAutoSaveContent } from '@reach/shared-types/communication-groups';
import { useEffect, useRef } from 'react';
import type { StripoTemplateData } from './stripo.types';

export const AUTO_SAVE_INTERVAL_MS = 30_000;

interface AutoSaveTick {
  lastWrittenContent: EmailAutoSaveContent | undefined;
  editorContent: EmailAutoSaveContent;
  isWriting: boolean;
}

function shouldWriteAutoSave({ lastWrittenContent, editorContent, isWriting }: AutoSaveTick): boolean {
  const baselineIsKnown = !!lastWrittenContent;
  const contentChanged =
    editorContent.html !== lastWrittenContent?.html || editorContent.css !== lastWrittenContent?.css;
  return baselineIsKnown && contentChanged && !isWriting;
}

async function readEditorContent(): Promise<EmailAutoSaveContent> {
  const actionsApi = window.StripoEditorApi?.actionsApi;
  if (!actionsApi?.getTemplateData) {
    throw new Error('Stripo editor is not available');
  }
  const { html, css } = await new Promise<StripoTemplateData>(resolve =>
    actionsApi.getTemplateData!(resolve),
  );
  return { html, css };
}

/** The baseline is read from the editor, not the stored email, because Stripo normalizes html. */
export const useStripoAutoSave = ({
  enabled,
  onAutoSave,
}: {
  enabled: boolean;
  onAutoSave: (content: EmailAutoSaveContent) => Promise<unknown>;
}) => {
  const onAutoSaveRef = useRef(onAutoSave);
  onAutoSaveRef.current = onAutoSave;

  // Stripo only exposes its content through an imperative API, so read it on an interval.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let lastWrittenContent: EmailAutoSaveContent | undefined;
    let isWriting = false;

    async function captureBaseline() {
      try {
        const baseline = await readEditorContent();
        lastWrittenContent ??= baseline;
      } catch (error) {
        console.warn('Email auto-save could not read the starting content:', error);
      }
    }

    async function writeAutoSave(editorContent: EmailAutoSaveContent) {
      isWriting = true;
      try {
        const saveAutoSave = onAutoSaveRef.current;
        await saveAutoSave(editorContent);
        lastWrittenContent = editorContent;
      } finally {
        isWriting = false;
      }
    }

    async function autoSave() {
      try {
        const editorContent = await readEditorContent();
        lastWrittenContent ??= editorContent;
        if (shouldWriteAutoSave({ lastWrittenContent, editorContent, isWriting })) {
          await writeAutoSave(editorContent);
        }
      } catch (error) {
        console.warn('Email auto-save failed, retrying on the next interval:', error);
      }
    }

    void captureBaseline();
    const intervalId = window.setInterval(() => void autoSave(), AUTO_SAVE_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [enabled]);
};
